import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Building2, Edit, TrendingUp, TrendingDown, DollarSign, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Bancos = () => {
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanco, setEditingBanco] = useState(null);
  const [novoSaldo, setNovoSaldo] = useState('');

  useEffect(() => {
    loadBancos();
  }, []);

  const loadBancos = async () => {
    try {
      const response = await api.get('/bancos');
      setBancos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar bancos');
    } finally {
      setLoading(false);
    }
  };

  const handleAjustarSaldo = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/bancos/${editingBanco.id}?saldo_atual=${parseFloat(novoSaldo)}`);
      toast.success('Saldo atualizado com sucesso!');
      setDialogOpen(false);
      setEditingBanco(null);
      setNovoSaldo('');
      loadBancos();
    } catch (error) {
      toast.error('Erro ao atualizar saldo');
    }
  };

  const handleEdit = (banco) => {
    setEditingBanco(banco);
    setNovoSaldo(banco.saldo_atual);
    setDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const totalGeral = bancos.reduce((sum, b) => sum + b.saldo_atual, 0);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="bancos-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center">
          <Building2 className="w-8 h-8 mr-3 text-green-600" />
          Bancos
        </h1>
        <p className="text-slate-600">Gerencie saldos e movimentações bancárias</p>
      </div>

      {/* Modal Ajuste de Saldo */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDialogOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full m-4" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-t-xl">
              <h2 className="text-xl font-bold">Atualizar Saldo - {editingBanco?.nome}</h2>
            </div>
            <form onSubmit={handleAjustarSaldo} className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-blue-900">Saldo Atual:</span>
                  <span className="text-lg font-bold text-green-600">{formatCurrency(editingBanco?.saldo_atual || 0)}</span>
                </div>
                <p className="text-xs text-blue-700">
                  O saldo é atualizado automaticamente quando você registra Entradas e Saídas pagas.
                </p>
              </div>

              <div>
                <Label>Editar Saldo Manualmente</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={novoSaldo}
                  onChange={(e) => setNovoSaldo(e.target.value)}
                  placeholder="0.00"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Digite o saldo real do extrato bancário. Este valor vai SUBSTITUIR o saldo atual.
                </p>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-green-600 to-emerald-600">
                  Atualizar Saldo
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Card de Total Geral */}
      <Card className="bg-gradient-to-br from-green-600 to-emerald-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 mb-2">Saldo Total em Bancos</p>
              <p className="text-4xl font-bold">{formatCurrency(totalGeral)}</p>
            </div>
            <DollarSign className="w-16 h-16 text-green-200" />
          </div>
        </CardContent>
      </Card>

      {/* Cards dos Bancos */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {bancos.map((banco) => (
            <Card key={banco.id} className="card-hover">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-bold text-slate-900 flex items-center justify-between">
                  <span>{banco.nome}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(banco)}
                    title="Ajustar Saldo"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-4 rounded-lg ${banco.saldo_atual >= 0 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200' : 'bg-gradient-to-br from-red-50 to-orange-50 border border-red-200'}">
                    <p className="text-xs font-semibold text-slate-600 mb-1">Saldo na Conta</p>
                    <p className={`text-2xl font-bold ${banco.saldo_atual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(banco.saldo_atual)}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Entradas:</span>
                      <span className="font-semibold text-green-600">{formatCurrency(banco.total_entradas || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Saídas:</span>
                      <span className="font-semibold text-red-600">{formatCurrency(banco.total_saidas || 0)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>ℹ️ Como funciona:</strong> O saldo de cada banco é atualizado automaticamente quando você registra Entradas (+ valor) e Saídas (- valor) pagas. 
          Clique em "Editar" para ajustar manualmente caso necessário (ex: saldo inicial, correções).
        </p>
      </div>
    </div>
  );
};

export default Bancos;
